/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import type { ThemeType } from "@packages/ui";
import {
  constantSemanticColors,
  cssMediaQueryMinWidthTablet,
  fontSizes,
  fontWeights,
  lineHeights,
  numericPercentages,
  radius,
  shades,
  spacing,
} from "@packages/ui";
import type { PropsWithChildren } from "react";
import { useMemo } from "react";

import {
  subtitlesWithControls,
  useMediaClass,
} from "../../../utils/media.emotion";

const defaultStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: shades.shade70,
  borderRadius: radius.m,
  bottom: "10vh",
  color: constantSemanticColors.onColorHighest,
  fontFamily: theme.fontFamilies.sansSerif,
  fontSize: fontSizes.bodyL,
  [cssMediaQueryMinWidthTablet]: {
    fontSize: fontSizes.headlineL,
    lineHeight: lineHeights.headlineL,
  },
  fontWeight: fontWeights.bodyXL,
  left: numericPercentages[50],
  lineHeight: lineHeights.bodyL,
  maxWidth: "80%",
  padding: spacing.m,
  position: "absolute",
  textAlign: "center",
  transform: "translateX(-50%)",
  transition: "transform 150ms ease-in-out",
  width: "max-content",
});

export type SubtitlesProps = PropsWithChildren<{
  showControls: boolean;
}>;

export const Subtitles = ({ children, showControls }: SubtitlesProps) => {
  const mediaClass = useMediaClass();

  const subtitlesStyle = useMemo(
    () => subtitlesWithControls[mediaClass],
    [mediaClass],
  );

  if (!children) return null;

  if (!mediaClass.includes("expanded") && !mediaClass.includes("fullscreen"))
    return null;

  return (
    <aside
      key={"subtitles"}
      css={[defaultStyle, showControls && subtitlesStyle]}
    >
      {children}
    </aside>
  );
};
